import React, { ReactNode, useMemo, useRef, useState } from "react";
import {
	cn,
	DataObserveKey,
	Icon,
	IconFinish,
	InlineText,
	Spinner,
	useUniqueId,
	VisualComponent,
} from "@siteimprove/fancylib";
import { combinedRefs, dataObserveKeyDiscriminator } from "../../../utils/shorthands";
import { KeyCode, keyCodeToListPosition } from "../../../utils/keyboard-nav-utils";
import { useLabTranslations } from "../../../translations/translations";
import { useDesignToken } from "../../context/theme/theme";
import { TextHighlight } from "../../text/text-highlight/text-highlight";
import { Trigger } from "../../../utils/dom-utils";
import * as scss from "./select-listbox.scss";

export type SelectListboxProps<T> = {
	/** Custom unique id */
	id: string;
	/** List of options or groups of options */
	items: SelectItem<T>[];
	/** Selected options */
	value: OptionItem<T>[];
	/** Function that returns true if two items are equal */
	compareFn?: CompareFunction<T>;
	/** Enables case sensitive highlight */
	caseSensitive?: boolean;
	/** Highlight options that contains this value */
	needle?: string;
	/** Toggles the option selection state */
	onToggleOption: (option: OptionItem<T>) => void;
	/** Select a set of options */
	onSelectBulk: (options: OptionItem<T>[]) => void;
	/** Deselect a set of options */
	onDeselectBulk: (options: OptionItem<T>[]) => void;
	/** Enables multiselection */
	multiple?: boolean;
	/** Show the create button as the last option */
	showCreateButton?: boolean;
	/** Callback for onCreate option event */
	onCreate?: (value: string) => void;
	/** Label to be displayed on the create option button  */
	createButtonLabel?: string;
	/** Display a loading spinner */
	loading?: boolean;
	/** If enabled, does not show selected items on top of the listbox when mounting */
	showSelectedItemsOnTop?: boolean;
	/** Force a re-rendering/sorting of the listbox; used after selecting a newly created item */
	reRenderListbox?: number;
	/** Custom renderer for options */
	optionRenderer?: (props: OptionProps<T>) => JSX.Element;
	/** Role of the list box */
	role?: "listbox" | "menu";
	/** Contains a title for the Listbox */
	"aria-label"?: string;
	/** Contains the ID of the button that opened it – if any (this will override any aria-label) */
	"aria-labelledby"?: string;
	/** Amount of time in milliseconds used to identify that the user has finished typing a string */
	keyboardTypingInterval?: number;
	/** Maximum number of items that can be selected */
	maxNumberOfItems?: number;
	/** Callback for onFocus event of option elements */
	onFocusOption?: (event: React.FocusEvent<HTMLDivElement>, value?: OptionItem<T>) => void;
	/** Reference to the first option element */
	firstOptionRef?: React.Ref<HTMLDivElement>;
	/** Optional heading of the Listbox */
	heading?: ReactNode;
} & VisualComponent &
	DataObserveKey;

export type SelectDto<K> = K extends readonly (infer T)[] ? T : K;

export type SelectItem<T> = OptionItem<T> | GroupContainerItem<T>;

export type OptionItem<T> = {
	/** Item value */
	value: SelectDto<T>;
	/** Item title */
	title: string;
	/** Item description */
	description?: string;
	/** Icon to render */
	icon?: JSX.Element;
	/** Disabled state */
	disabled?: boolean;
	/** Item role */
	role?: "option" | "menuitem";
	/** Optional item key (default is the title) */
	key?: string;
} & DataObserveKey;

export type GroupContainerItem<T> = {
	/** Group heading string */
	heading: string;
	/** List of items */
	items: OptionItem<T>[];
};

export type CompareFunction<T> = (a: SelectDto<T>, b: SelectDto<T>) => boolean;

export function isOptionItem<T>(item: SelectItem<T>): item is OptionItem<T> {
	return "value" in item;
}

export function SelectListbox<T>(props: SelectListboxProps<T>): JSX.Element {
	const {
		caseSensitive = false,
		className,
		compareFn,
		createButtonLabel,
		firstOptionRef,
		heading,
		id,
		items = [],
		keyboardTypingInterval = 500,
		loading = false,
		maxNumberOfItems,
		multiple = false,
		needle = null,
		onSelectBulk,
		onDeselectBulk,
		onToggleOption,
		onCreate,
		onFocusOption,
		optionRenderer,
		role = "listbox",
		showCreateButton = false,
		showSelectedItemsOnTop,
		reRenderListbox,
		style,
		value: selectedOptions,
	} = props;

	const sortedItems = useMemo<SelectItem<T>[]>(
		() => (showSelectedItemsOnTop ? sortSelectedItemsOnTop(items) : items),
		[showSelectedItemsOnTop, items, reRenderListbox]
	);

	const currentTrigger = useRef(Trigger.Keyboard);
	const optionsRefs: HTMLDivElement[] = [];
	const [activeDescendantId, setActiveDescendantId] = useState<string | undefined>();
	const [focusFilter, setFocusFilter] = useState<string>("");
	const [lastKeyboardEventAt, setLastKeyboardEventAt] = useState<number>(0);
	const optionsItems = useMemo<OptionItem<T>[]>(() => flattenOptions(sortedItems), [sortedItems]);
	const selectedOptionsAreBeingListed = useMemo<boolean>(
		() => selectedOptions.some((option) => indexOfOption(optionsItems, option, compareFn) > -1),
		[optionsItems, selectedOptions]
	);

	// The first focusable option is the first option that is not disabled.
	// If there are selected options being listed, pick the first one.
	const firstFocusableOption = optionsItems
		.filter((option) => !option.disabled)
		.find((option) => selectedOptionsAreBeingListed === isSelected(option));

	const handleKeyboardNavigation = (event: React.KeyboardEvent<HTMLDivElement>) => {
		currentTrigger.current = Trigger.Keyboard;

		// Should do nothing if the default action has been cancelled
		if (event.defaultPrevented) {
			return;
		}

		// handler for Ctrl+Arrows
		// select all/deselect all
		const isCtrlAKey = event.ctrlKey && event.keyCode == KeyCode.A;
		if (multiple && isCtrlAKey) {
			event.preventDefault();
			const areVisibleOptionsSelected = optionsItems.every(
				(option) => indexOfOption(selectedOptions, option, compareFn) > -1
			);
			return areVisibleOptionsSelected ? onDeselectBulk(optionsItems) : onSelectBulk(optionsItems);
		}

		// handler for navigation keys, such as Arrows, Home and End
		const currentIndex = optionsRefs.indexOf(event.target as HTMLDivElement);
		const targetIndex = keyCodeToListPosition(
			"vertical",
			event.keyCode,
			currentIndex,
			optionsRefs.length - 1
		);
		const isNavigationKey = targetIndex !== null;
		if (isNavigationKey) {
			event.preventDefault();
			optionsRefs[targetIndex].focus();
			if (multiple && event.shiftKey) {
				const selectionRange: OptionItem<T>[] = getSelectionRange(
					optionsItems,
					selectedOptions,
					targetIndex,
					compareFn
				);
				onSelectBulk(selectionRange);
			}
			return;
		}

		// handler for A-Z and 0-9
		//  - move focus to the first Option that begins with the sequence of letters typed
		//  - meset when the user has not typed any letters for a few seconds (keyboardTypingInterval)
		if (isKeyCodeSearchableCharacter(event.keyCode)) {
			event.preventDefault();
			const filter = keyCodeToFocusFilter(
				event.keyCode,
				focusFilter,
				lastKeyboardEventAt,
				keyboardTypingInterval
			);
			setFocusFilter(filter);
			setLastKeyboardEventAt(Date.now());
			optionsRefs
				.find((ref) => ref.getAttribute("data-content")?.toUpperCase().startsWith(filter))
				?.focus();
			return;
		}
	};

	const handleOnClick = (event: React.MouseEvent<HTMLDivElement>, item: OptionItem<T>) => {
		if (multiple && event.shiftKey) {
			const closestOptionElement = (event.target as Element).closest("[role='option']");
			const currentIndex: number = optionsRefs.indexOf(closestOptionElement as HTMLDivElement);
			const selectionRange: OptionItem<T>[] = getSelectionRange(
				optionsItems,
				selectedOptions,
				currentIndex,
				compareFn
			);
			onSelectBulk(selectionRange);
		} else {
			onToggleOption(item);
		}
	};

	const renderOption = (option: OptionItem<T>) => {
		// if there is no selected options being listed, the first option must
		// be accessible by TAB navigation
		const isFirstOption = isOptionEqual(option, firstFocusableOption, compareFn);
		const tabIndex = !selectedOptionsAreBeingListed && isFirstOption ? 0 : undefined;
		const isOptionSelected = isSelected(option);
		const innerRef = (ref: HTMLDivElement) => ref && optionsRefs.push(ref);
		const defaultOptionProps: OptionProps<T> = {
			caseSensitive,
			disabled:
				option.disabled || (maxNumberOfItems && maxNumberOfItems <= selectedOptions.length)
					? !isOptionSelected
					: false,
			innerRef: isFirstOption && firstOptionRef ? combinedRefs(innerRef, firstOptionRef) : innerRef,
			isSelected: isOptionSelected,
			item: option,
			multiSelectable: multiple,
			needle,
			onClick: handleOnClick,
			tabIndex,
			onFocus: (e) => {
				setActiveDescendantId(e.target.id);
				onFocusOption?.(e);
			},
			onSelect: onToggleOption,
		};

		// calls custom Option renderer, if any
		if (optionRenderer) {
			return optionRenderer(defaultOptionProps);
		}

		return <Option {...defaultOptionProps} key={option.key ?? option.title} />;
	};

	return (
		<div
			aria-label={props["aria-label"]}
			aria-labelledby={props["aria-labelledby"]}
			className={cn(scss.listbox, className)}
			aria-activedescendant={activeDescendantId}
			aria-multiselectable={role === "listbox" ? multiple : undefined}
			id={id}
			onKeyDown={handleKeyboardNavigation}
			onMouseDown={() => (currentTrigger.current = Trigger.Mouse)}
			role={role}
			tabIndex={-1}
			style={style}
		>
			{loading ? (
				<div className={cn(scss.option)}>
					<Spinner variant="dark" size="medium" />
				</div>
			) : (
				<>
					{heading && (
						<div className={cn(scss.listboxHeading)}>
							{typeof heading !== "string" ? (
								heading
							) : (
								<InlineText tone="subtle" size="small" aria-hidden="true">
									{heading}
								</InlineText>
							)}
						</div>
					)}
					{sortedItems.map((item) =>
						isOptionItem(item)
							? renderOption(item)
							: item.items.length > 0 && (
									<GroupContainer key={item.heading} heading={item.heading}>
										{item.items.map(renderOption)}
									</GroupContainer>
							  )
					)}
					{showCreateButton && onCreate && needle && (
						<CreateOption
							label={createButtonLabel}
							needle={needle}
							onCreate={onCreate}
							ref={(ref) => ref && optionsRefs.push(ref)}
							tabIndex={optionsItems.length > 0 ? -1 : 0}
							data-observe-key={dataObserveKeyDiscriminator(
								props["data-observe-key"],
								"Select",
								"CreateOptionButton"
							)}
						/>
					)}
				</>
			)}
		</div>
	);

	function isSelected(option: OptionItem<T>): boolean {
		return indexOfOption(selectedOptions, option, compareFn) > -1;
	}

	function sortSelectedItemsOnTop(options: SelectItem<T>[]) {
		const sortedList: SelectItem<T>[] = options.map((option) => ({ ...option }));
		sortedList.sort(compareBySelectedState).map((option) => {
			if (!isOptionItem(option)) {
				const sortedGroupItems = [...option.items];
				sortedGroupItems.sort(compareBySelectedState);
				option.items = sortedGroupItems;
			}
			return option;
		});

		return sortedList;
	}

	function compareBySelectedState(a: SelectItem<T>, b: SelectItem<T>): -1 | 0 | 1 {
		const isASelected = isOptionItem(a) ? isSelected(a) : a.items.some(isSelected);
		const isBSelected = isOptionItem(b) ? isSelected(b) : b.items.some(isSelected);
		if (isASelected && !isBSelected) return -1;
		if (!isASelected && isBSelected) return 1;
		return 0;
	}
}

export function BaseSelectOption<T>(props: BaseSelectOptionProps<T>): JSX.Element {
	const {
		children,
		className,
		disabled,
		innerRef,
		isSelected,
		item,
		multiSelectable,
		onClick,
		onFocus,
		onSelect,
		style,
		tabIndex,
	} = props;
	const { title, role = "option" } = item;
	const optionId = useUniqueId("list-box-option");

	return (
		<div
			data-observe-key={item["data-observe-key"]}
			aria-selected={!multiSelectable ? isSelected : undefined}
			aria-checked={
				multiSelectable ? ("aria-checked" in props ? props["aria-checked"] : isSelected) : undefined
			}
			aria-disabled={disabled}
			className={cn(scss.option, className)}
			data-content={title}
			id={optionId}
			onClick={(event) => !disabled && onClick(event, item)}
			onFocus={(event) => onFocus(event, item)}
			onKeyDown={(event) => {
				// Enter key selects the item
				if (
					!disabled &&
					(event.keyCode === KeyCode.Enter || (event.keyCode === KeyCode.Space && multiSelectable))
				) {
					event.preventDefault();
					onSelect(item);
					return;
				}
			}}
			ref={innerRef}
			role={role}
			style={style}
			tabIndex={tabIndex !== undefined ? tabIndex : isSelected ? 0 : -1}
		>
			{children}
		</div>
	);
}

function Option<T>(props: OptionProps<T>): JSX.Element {
	const { caseSensitive, disabled, item, isSelected, multiSelectable, needle } = props;
	const { title, description, icon } = item;
	const { ColorBorderInteractiveSelected } = useDesignToken();
	return (
		<BaseSelectOption {...props}>
			<div className={cn(scss.optionMainContainer)}>
				<div className={cn(scss.optionTitleContainer)}>
					{icon}
					<InlineText
						tone={disabled ? "subtle" : "neutralDark"}
						lineHeight="multi-line"
						emphasis={isSelected ? "medium" : "normal"}
					>
						<TextHighlight value={title} needle={needle} caseSensitive={caseSensitive} />
					</InlineText>
				</div>
				{multiSelectable && isSelected && (
					<div>
						<Icon size="medium" fill={ColorBorderInteractiveSelected}>
							<IconFinish />
						</Icon>
					</div>
				)}
			</div>
			{description && (
				<InlineText size="xSmall" tone="subtle" lineHeight="multi-line">
					<TextHighlight value={description} needle={needle} caseSensitive={caseSensitive} />
				</InlineText>
			)}
		</BaseSelectOption>
	);
}

const GroupContainer = (props: GroupContainerProps): JSX.Element => {
	const { children, heading } = props;
	const headingId = useUniqueId("list-box-group-container");
	return (
		<div role="group" aria-labelledby={headingId}>
			<InlineText
				tone="subtle"
				size="large"
				aria-hidden="true"
				className={cn(scss.groupContainerHeading)}
				id={headingId}
			>
				{heading}
			</InlineText>
			{children}
		</div>
	);
};

const CreateOption = React.forwardRef<HTMLDivElement | null, CreateOptionProps>(
	(props: CreateOptionProps, ref): JSX.Element => {
		const { label, needle, onCreate, tabIndex } = props;

		const i18n = useLabTranslations();
		const textContent = `${i18n.create} ${needle}`;

		return (
			<div
				data-observe-key={props["data-observe-key"]}
				aria-selected={false}
				className={cn(scss.option)}
				data-content={textContent}
				onClick={() => onCreate(needle)}
				onKeyDown={(event) => {
					// Enter key creates the item
					if (event.keyCode === KeyCode.Enter) {
						event.preventDefault();
						onCreate(needle);
						return;
					}
				}}
				ref={ref}
				role="option"
				tabIndex={tabIndex}
			>
				<div className={cn(scss.optionMainContainer)}>
					<div className={cn(scss.optionTitleContainer)}>
						<InlineText className={cn(scss.createLabel)}>{label ?? i18n.create}</InlineText>
						<InlineText emphasis="medium">
							<q>{needle}</q>
						</InlineText>
					</div>
				</div>
			</div>
		);
	}
);

function isKeyCodeSearchableCharacter(keyCode: number): boolean {
	const typedChar = String.fromCharCode(keyCode);
	const searchableCharacters = /[A-Z0-9]/i;
	return searchableCharacters.test(typedChar);
}

function keyCodeToFocusFilter(
	keyCode: number,
	currentFilter: string,
	lastKeyboardEventAt: number,
	keyboardTypingInterval: number
): string {
	const typedChar = String.fromCharCode(keyCode);
	const timeSinceLastKeyboardEvent = Date.now() - lastKeyboardEventAt;
	const clearFocusFilter = timeSinceLastKeyboardEvent >= keyboardTypingInterval;
	return clearFocusFilter ? typedChar : currentFilter + typedChar;
}

function getSelectionRange<T>(
	options: OptionItem<T>[],
	currentSelection: OptionItem<T>[],
	targetIndex: number,
	compareFn?: CompareFunction<T>
): OptionItem<T>[] {
	const beginOfSelection = currentSelection.length
		? indexOfOption(options, currentSelection[0], compareFn)
		: -1;
	const endOfSelection = currentSelection.length
		? indexOfOption(options, currentSelection[currentSelection.length - 1], compareFn)
		: -1;
	return options.slice(
		Math.min(beginOfSelection, targetIndex),
		Math.max(endOfSelection, targetIndex) + 1
	);
}

export function indexOfOption<T>(
	options: OptionItem<T>[],
	item: OptionItem<T>,
	compareFn?: CompareFunction<T>
): number {
	return options.findIndex((option) => isOptionEqual(option, item, compareFn));
}

export function isOptionEqual<T>(
	a: OptionItem<T> | undefined,
	b: OptionItem<T> | undefined,
	compareFn?: CompareFunction<T>
): boolean {
	return a && b && compareFn ? compareFn(a.value, b.value) : a?.value === b?.value;
}

export function areOptionsEqual<T>(
	set1: OptionItem<T>[],
	set2: OptionItem<T>[],
	compareFn?: CompareFunction<T>
): boolean {
	return (
		set1.length == set2.length &&
		set1.every((a) => set2.some((b) => isOptionEqual(a, b, compareFn)))
	);
}

export function flattenOptions<T>(items: SelectItem<T>[]): OptionItem<T>[] {
	const flattend: OptionItem<T>[] = [];
	for (const item of items) {
		if (isOptionItem(item)) {
			flattend.push(item);
		} else {
			flattend.push(...item.items);
		}
	}
	return flattend;
}

export type OptionProps<T> = {
	caseSensitive: boolean;
	disabled: boolean;
	innerRef?: React.Ref<HTMLDivElement>;
	isSelected: boolean;
	item: OptionItem<T>;
	multiSelectable: boolean;
	needle: string | null;
	onClick: (event: React.MouseEvent<HTMLDivElement>, value: OptionItem<T>) => void;
	onFocus: (event: React.FocusEvent<HTMLDivElement>, value?: OptionItem<T>) => void;
	onSelect: (value: OptionItem<T>) => void;
	tabIndex?: 0 | -1 | 1;
};

export type BaseSelectOptionProps<T> = React.PropsWithChildren<OptionProps<T>> & VisualComponent;

type CreateOptionProps = {
	label?: string;
	needle: string;
	onCreate: (value: string) => void;
	tabIndex?: 0 | -1 | 1;
} & DataObserveKey;

type GroupContainerProps = {
	heading: string;
	children: ReactNode;
};
